import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { TreeSelect } from "primereact/treeselect";
import CustomeButton from "~/components/Button";
import useTenants from "../useTenants";
import { useEffect, useState } from "react";
import { RadioButton } from "primereact/radiobutton";
import ReactCountryFlag from "react-country-flag";
import {
  Entity,
  ManageEntityAccessPayload,
  ManageEntityModalProps,
  SelectedEntityOption,
  SelectedNodeKeys,
  ServiceLineSelection,
  TreeNodes,
} from "../types/tenants";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

const ManageEntityServiceLineModal = ({
  visible,
  closeModal,
  toastRef,
  isEdit,
  entity,
  fetchTenantEntities,
  assignedEntities,
}: ManageEntityModalProps) => {
  const { id } = useParams();
  const [entityList, setEntityList] = useState<SelectedEntityOption[]>([]);
  const [totalEntities, setTotalEntities] = useState<Entity[]>([]);
  const [selectedEntityOption, setSelectedEntityOption] =
    useState<SelectedEntityOption | null>(null);
  const [selectedEntity, setSelectedEntity] = useState<Entity | null>(null);
  const [expirationDate, setExpirationDate] = useState<Date | null>(null);
  const [nodes, setNodes] = useState<TreeNodes[]>([]);
  const [selectedNodeKeys, setSelectedNodeKeys] =
    useState<SelectedNodeKeys | null>(null);
  const [serviceLineSelection, setServiceLineSelection] =
    useState<ServiceLineSelection | null>(null);
  const [loading, setLoading] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(false);
  const [error, setError] = useState("");

  const { fetchCustomerEntities, addLspEntityAcceess, updateLspEntityAcceess } =
    useTenants();
  const queryClient = useQueryClient();

  const fetchEntities = async () => {
    try {
      setEntitiesLoading(true);
      const response = await fetchCustomerEntities();
      const entityList = response?.data || [];
      //transform entities data for dropdown
      const entityOptions: SelectedEntityOption[] = entityList.map(
        (entity) => ({
          name: entity.name,
          code: entity.id,
          country: entity.country,
        }),
      );
      setTotalEntities(entityList);
      setEntityList(entityOptions);
      setEntitiesLoading(false);
    } catch (error) {
      console.error(error);
      setEntitiesLoading(false);
    }
  };

  const selectEntityHandler = (e: DropdownChangeEvent) => {
    setSelectedEntityOption(e.value);
    buildTreeNodes(e.value);
    const isAlreadyAssigned = assignedEntities.includes(e.value.code);
    if (isAlreadyAssigned) {
      setError("This entity is already assigned");
      return;
    }
    setError("");
  };

  const serviceLineChangeHandler = (e: DropdownChangeEvent) => {
    const next: SelectedNodeKeys = { ...e.value };
    setSelectedNodeKeys(next);
    setServiceLineSelection(buildFinalSelection(next));
  };

  const buildFinalSelection = (selection: SelectedNodeKeys) => {
    const result: ServiceLineSelection = {};

    selectedEntity?.services.forEach((sl) => {
      const slId = sl.id;

      // Check if service line or any process is selected
      const selectedProcesses = sl.process_templates
        .filter((p) => selection[p.id]?.checked)
        .map((p) => p.process_category);

      if (selectedProcesses.length > 0) {
        result[slId] = selectedProcesses;
      }
    });
    return result;
  };

  const buildFinalSelectionForEdit = (selection: SelectedNodeKeys) => {
    const result: ServiceLineSelection = {};
    const fullEntity = totalEntities.find((e) => e.id === entity?.id);
    fullEntity?.services.forEach((sl) => {
      const slId = sl.id;

      // Check if service line or any process is selected
      const selectedProcesses = sl.process_templates
        .filter((p) => selection[p.id]?.checked)
        .map((p) => p.process_category);

      if (selectedProcesses.length > 0) {
        result[slId] = selectedProcesses;
      }
    });
    return result;
  };

  const assignHandler = async () => {
    try {
      if (!selectedEntity || !serviceLineSelection) return;
      setLoading(true);
      const services = buildPayloadServices(serviceLineSelection);
      const payload: ManageEntityAccessPayload = {
        entityId: selectedEntity?.id,
        services,
        ...(expirationDate && {
          expiresAt: expirationDate?.toISOString(),
        }),
      };
      if (id) {
        const response = await addLspEntityAcceess(id, payload);
        if (response.success) {
          toastRef?.current?.show({
            severity: "success",
            icon: "pi pi-check",
            summary: `'${selectedEntity?.name}' has been successfully added to the Local Team`,
            life: 5000,
          });
          closeModal();
          await fetchTenantEntities({
            pageIndex: 0,
            pageSize: 10,
            sortBy: "name",
            sortOrder: "asc",
            skipCache: true,
          });
          // Invalidate audit logs cache to refresh after addition
          queryClient.invalidateQueries({
            queryKey: ["tenant-entity-access-audit", id],
          });
        }
      }
    } catch (error) {
      console.error(error);
      if (toastRef?.current) {
        toastRef?.current?.show({
          severity: "error",
          icon: "pi pi-times-circle",
          summary: "Oops! Something went wrong while adding access.",
          detail: "Please try again.",
          life: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const updateHandler = async () => {
    try {
      if (!selectedEntity || !serviceLineSelection) return;
      setLoading(true);
      const services = buildPayloadServices(serviceLineSelection);
      const payload: ManageEntityAccessPayload = {
        entityId: selectedEntity.id,
        services,
        ...(expirationDate && {
          expiresAt: expirationDate?.toISOString(),
        }),
      };
      if (id) {
        const response = await updateLspEntityAcceess(id, payload);
        if (response.success) {
          toastRef?.current?.show({
            severity: "success",
            icon: "pi pi-check",
            summary: `Local Team access for '${selectedEntity?.name}' has been successfully updated.`,
            life: 5000,
          });
          closeModal();
          await fetchTenantEntities({
            pageIndex: 0,
            pageSize: 10,
            sortBy: "name",
            sortOrder: "asc",
            skipCache: true,
          });
          // Invalidate audit logs cache to refresh after update
          queryClient.invalidateQueries({
            queryKey: ["tenant-entity-access-audit", id],
          });
        }
      }
    } catch (error) {
      console.error(error);
      if (toastRef?.current) {
        toastRef?.current?.show({
          severity: "error",
          icon: "pi pi-times-circle",
          summary: "Oops! Something went wrong while adding access.",
          detail: "Please try again.",
          life: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const buildPayloadServices = (value: ServiceLineSelection) => {
    const services = Object.entries(value).map(
      ([serviceId, processCategories]) => ({
        serviceId,
        processCategories: processCategories.length ? processCategories : [],
      }),
    );
    return services;
  };

  const HeaderTemplate = () => {
    const headerTitle = isEdit ? "Edit Entity Access" : "Add New Entity Access";
    const description = isEdit
      ? "Assign or modify the customer entities and service lines available to this Local Team."
      : "Assign customer entities and service lines to this Local Team.";
    return (
      <div className="flex gap-2 justify-content-between">
        <div className="flex flex-column gap-1 w-full">
          <h4 className="m-0 title"> {headerTitle} </h4>
          <p className="m-0 description">{description}</p>
        </div>

        <CustomeButton icon="pi pi-times close-icon" onClick={closeModal} />
      </div>
    );
  };

  const valueTemplate = (nodes: any) => {
    // With the nested structure, filter out the selectAll parent and
    // extract service line names from selected nodes
    const allNodes = nodes || [];
    const filtered = allNodes.filter((n: TreeNodes) => n.key !== "selectAll");

    if (filtered.length === 0)
      return "Choose the service lines and processes that apply to this access";

    return filtered.map((n: TreeNodes) => n.label).join(", ");
  };

  const OptionsTemplate = (option: SelectedEntityOption) => {
    return (
      <div className="flex align-items-center gap-2">
        <RadioButton
          checked={
            !!(
              selectedEntityOption && selectedEntityOption.code === option.code
            )
          }
        />
        <ReactCountryFlag countryCode={option.country || ""} svg className="" />
        <span>
          {option.name} ({option.country})
        </span>
      </div>
    );
  };

  const FooterTemplate = () => {
    return (
      <div className="flex justify-content-end gap-2">
        <CustomeButton
          label="Discard"
          variant="outlined"
          className="cancel-button"
          onClick={closeModal}
        />
        <CustomeButton
          label={isEdit ? "Save Changes" : "Add Access"}
          variant="contained"
          className="assign-button"
          onClick={isEdit ? updateHandler : assignHandler}
          disabled={
            !selectedEntityOption ||
            !(
              serviceLineSelection && Object.keys(serviceLineSelection).length
            ) ||
            loading ||
            error
          }
          loading={loading}
        />
      </div>
    );
  };

  const buildPreselectedNodes = (entity: Entity) => {
    const selected: any = {};

    const fullEntity = totalEntities.find((e) => e.id === entity.id);

    if (!fullEntity) return selected;
    entity.services?.forEach((accessService) => {
      const service = fullEntity.services?.find(
        (s) => s.id === accessService.serviceId,
      );

      if (!service) return;

      const matchedProcessIds: string[] = [];
      accessService.processCategories?.forEach((category) => {
        const matchedProcesses = service.process_templates?.filter(
          (pt) => pt.process_category === category,
        );

        if (matchedProcesses.length) {
          matchedProcesses.forEach((matchedProcess) => {
            selected[matchedProcess.id] = { checked: true };
            matchedProcessIds.push(matchedProcess.id);
          });
        }
      });

      // Check if all processes of this service are selected
      const allProcessesSelected =
        service.process_templates?.length === matchedProcessIds.length;
      if (matchedProcessIds.length > 0) {
        if (allProcessesSelected) {
          selected[service.id] = { checked: true };
        } else {
          selected[service.id] = { checked: false, partiallyChecked: true };
        }
      }
    });

    // Check if all service lines and processes are selected
    const totalProcessCount = fullEntity.services.reduce(
      (acc, sl) => acc + (sl.process_templates?.length || 0),
      0,
    );

    const checkedProcessCount = Object.keys(selected).filter(
      (key) => selected[key]?.checked && key !== "selectAll",
    ).length;

    const totalItemsCount = fullEntity.services.length + totalProcessCount;
    const hasAnySelected = Object.keys(selected).length > 0;

    if (checkedProcessCount === totalItemsCount && totalItemsCount > 0) {
      selected["selectAll"] = { checked: true };
    } else if (hasAnySelected) {
      selected["selectAll"] = { checked: false, partiallyChecked: true };
    }

    return selected;
  };
  useEffect(() => {
    fetchEntities();
  }, []);

  useEffect(() => {
    if (isEdit && entity) {
      prepareEditData(entity);
    }
  }, [isEdit, entity, totalEntities]);

  const prepareEditData = (entity: Entity) => {
    setSelectedEntity(entity);
    const optionSelected = {
      name: entity.name,
      code: entity.id,
      country: entity.country,
    };
    setSelectedEntityOption(optionSelected);
    buildTreeNodes(optionSelected);
    const preselected = buildPreselectedNodes(entity);
    setSelectedNodeKeys(preselected);
    setServiceLineSelection(buildFinalSelectionForEdit(preselected));
    if (entity.expirationDate) {
      setExpirationDate(new Date(entity.expirationDate));
    }
  };

  const buildTreeNodes = (selectedEntityOption: SelectedEntityOption) => {
    const entity = totalEntities.find(
      (entity) => entity.id === selectedEntityOption.code,
    );
    if (entity) {
      setSelectedEntity(entity);
    }
    //transform service line/ processes data for tree select
    const serviceLines = entity?.services || [];
    const processCount = serviceLines.reduce(
      (acc, sl) => acc + sl.process_templates.length,
      0,
    );
    const treeNodes: TreeNodes[] = [
      {
        key: "selectAll",
        label: `All Service Lines & Processes (${processCount})`,
        children: serviceLines.map((serviceLine) => ({
          key: serviceLine.id,
          label: serviceLine.name,
          children: serviceLine.process_templates.map((process) => ({
            key: process.id,
            label: process.name,
          })),
        })),
      },
    ];
    setNodes(treeNodes);
    return treeNodes;
  };

  return (
    <Dialog
      visible={visible}
      style={{ width: "760px" }}
      onHide={closeModal}
      className="manage-entity-service-line-modal"
      header={HeaderTemplate}
      draggable={false}
      footer={FooterTemplate}
    >
      <div className="flex flex-column gap-3">
        <div className="flex flex-column gap-2">
          <label className="entity-label" htmlFor="entity-dropdown">
            Entity *
          </label>
          <Dropdown
            id="entity-dropdown"
            value={selectedEntityOption}
            options={entityList}
            onChange={selectEntityHandler}
            optionLabel="name"
            placeholder="Select the customer entity this Local Team will support"
            itemTemplate={OptionsTemplate}
            filter
            loading={entitiesLoading}
            disabled={entitiesLoading || isEdit}
          />
          {error && <p className="error-message m-0">{error}</p>}
        </div>

        <div className="flex flex-column gap-2">
          <label className="process-label" htmlFor="process-dropdown">
            Service Lines & Processes *
          </label>
          <TreeSelect
            id="process-dropdown"
            placeholder="Choose the service lines and processes that apply to this access"
            disabled={!selectedEntityOption}
            options={nodes}
            selectionMode="checkbox"
            metaKeySelection={false}
            onChange={serviceLineChangeHandler}
            value={selectedNodeKeys}
            valueTemplate={valueTemplate}
          />
        </div>

        <div className="flex flex-column gap-2">
          <label className="flex align-items-center gap-1">
            Expiration Date{" "}
            <span className="expiration-date-info flex align-items-center">
              <i className="pi pi-info-circle" />
            </span>
            <Tooltip
              target=".expiration-date-info"
              content="Define when this access should expire. The Local Team will lose access after this date."
              position="right"
            />
          </label>
          <Calendar
            id="calendar"
            placeholder="Select the expiration date for this access"
            minDate={new Date()}
            value={expirationDate}
            onChange={(e) => {
              if (e.value) {
                setExpirationDate(e.value);
              }
            }}
          />
          <span className="pi pi-calendar expiration-calendar-icon"></span>
        </div>
      </div>
    </Dialog>
  );
};

export default ManageEntityServiceLineModal;
